import { useMemo } from 'react';

import { CouponFragment, DoorToDoor__BundleEnum } from '@graphql/platform';

import {
  couponDiscountAmount,
  isAppointmentCoupon,
} from '@root/components/checkout/cart/challenger_promo';
import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { StorageCheckoutData } from '@root/components/checkout/data';

import { formatCurrency } from '@utils/formatters';
import { useLastDefined } from '@utils/hooks/use_last_defined';

import { useChallengerPackingAmount } from './use_challenger_packing_amount';

const DISPOSAL_FROM_AMOUNT = 50;

type Args = {
  bundleKind?: DoorToDoor__BundleEnum;
  coupon?: CouponFragment;
  dateScheduled?: StorageCheckoutData['dateScheduled'];
  disposalSelected?: boolean;
  packingLabelText?: string;
};

export interface ChallengerPricingRows {
  appointmentPromoDiscount: number;
  disposalAmount: number;
  gasFeeAmount: number;
  packingAmount: number | undefined;
  packingLabel: string | null;
  packingPriceDisplay: string | null;
  packingQuoteError?: Error;
  packingQuoteLoading: boolean;
  pickupFeesBeforePromo: number | undefined;
  pickupTotal: number | undefined;
  pickupTotalDisplay: string;
  scheduledArrivalAmount: number;
  showPackingLine: boolean;
  showPickupFees: boolean;
}

/**
 * Shared pick-up fee rows for challenger booking summaries (packing, window,
 * gas, disposal, appointment promos). Excludes storage-plan promo math.
 */
export const useChallengerPricingRows = ({
  bundleKind,
  coupon,
  dateScheduled,
  disposalSelected,
  packingLabelText = 'Packing assistance',
}: Args): ChallengerPricingRows => {
  const { pricingSummary, defaultPricingSummary } = useStorageCheckoutContext();
  // Gas fee is ZIP/quote-based and does not need to blank while plan size refetches.
  const stablePricingSummary =
    useLastDefined(pricingSummary) ?? defaultPricingSummary;
  const {
    amount: packingQuoteAmount,
    loading: packingQuoteLoading,
    error: packingQuoteError,
  } = useChallengerPackingAmount();

  return useMemo(() => {
    const wantsPacking = bundleKind === DoorToDoor__BundleEnum.PickupAndPacking;
    const packingResolved =
      wantsPacking && !packingQuoteLoading && packingQuoteAmount != null;
    const packingAmount = packingResolved ? packingQuoteAmount : undefined;
    const packingQuoteReady =
      !wantsPacking || packingAmount != null || packingQuoteError != null;

    const scheduledArrivalAmount = dateScheduled?.feeAmount?.value ?? 0;
    const gasFeeAmount = stablePricingSummary?.gasFeeAmount ?? 0;
    const disposalAmount = disposalSelected ? DISPOSAL_FROM_AMOUNT : 0;
    const pickupFeesBeforePromo = packingQuoteReady
      ? (packingAmount ?? 0) +
        scheduledArrivalAmount +
        gasFeeAmount +
        disposalAmount
      : undefined;

    const appointmentPromoDiscount =
      coupon && isAppointmentCoupon(coupon) && pickupFeesBeforePromo != null
        ? couponDiscountAmount(coupon, pickupFeesBeforePromo)
        : 0;

    const pickupTotal =
      pickupFeesBeforePromo != null
        ? Math.max(0, pickupFeesBeforePromo - appointmentPromoDiscount)
        : undefined;

    const showPackingLine =
      wantsPacking &&
      (packingQuoteLoading ||
        packingQuoteError != null ||
        packingAmount != null);

    const showPickupFees =
      showPackingLine ||
      scheduledArrivalAmount > 0 ||
      gasFeeAmount > 0 ||
      !!disposalSelected ||
      appointmentPromoDiscount > 0;

    let packingPriceDisplay: string | null = null;
    if (showPackingLine) {
      if (packingQuoteLoading) {
        packingPriceDisplay = '—';
      } else if (packingAmount != null) {
        packingPriceDisplay = formatCurrency(packingAmount);
      } else if (packingQuoteError) {
        packingPriceDisplay = formatCurrency(0);
      } else {
        packingPriceDisplay = '—';
      }
    }

    let pickupTotalDisplay = '—';
    if (pickupTotal != null) {
      pickupTotalDisplay = formatCurrency(pickupTotal);
    }

    return {
      appointmentPromoDiscount,
      disposalAmount,
      gasFeeAmount,
      packingAmount,
      packingLabel: wantsPacking ? packingLabelText : null,
      packingPriceDisplay,
      packingQuoteError,
      packingQuoteLoading,
      pickupFeesBeforePromo,
      pickupTotal,
      pickupTotalDisplay,
      scheduledArrivalAmount,
      showPackingLine,
      showPickupFees,
    };
  }, [
    bundleKind,
    coupon,
    dateScheduled?.feeAmount?.value,
    disposalSelected,
    packingLabelText,
    packingQuoteAmount,
    packingQuoteError,
    packingQuoteLoading,
    stablePricingSummary?.gasFeeAmount,
  ]);
};
